import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Users, ShieldCheck, Store, ArrowRight, Gift, Zap, Clock, CreditCard, Sparkles, Calendar, HelpCircle, ChevronDown } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { FAQChatBot } from "@/components/FAQChatBot";
import { supabase } from "@/integrations/supabase/client";

// Merchant-focused landing page

interface PlatformStats {
  totalMerchants: number;
  totalRevenue: number;
  totalCustomers: number;
}

const formatCompactNumber = (num: number): string => {
  if (num >= 1000000) {
    return `₹${(num / 1000000).toFixed(1)}M+`;
  } else if (num >= 1000) {
    return `₹${(num / 1000).toFixed(0)}K+`;
  }
  return `₹${num.toLocaleString()}`;
};

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
function Index() {
  const [stats, setStats] = useState<PlatformStats | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("get-platform-stats");
        if (!error && data) {
          setStats(data);
        }
      } catch (err) {
        console.error("Failed to fetch platform stats:", err);
      }
    };
    fetchStats();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO url="/" />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[80vh] flex items-center bg-gradient-to-br from-primary/10 via-background to-accent/10 overflow-hidden">
          <div className="container py-20">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-8">
                <Sparkles className="w-4 h-4" />
                One-click group gifting & payments
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-foreground leading-tight mb-6">
                Your One-Stop <span className="text-primary">Group Purchase</span> Solution
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Let friends chip in together. One link. One click. Done.
              </p>
              
              {/* Platform badges */}
              <div className="flex flex-wrap justify-center gap-3 mb-10">
                <span className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm font-medium">Shopify</span>
                <span className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm font-medium">WooCommerce</span>
                <span className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm font-medium">Custom Stores</span>
                <span className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm font-medium">API</span>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="text-lg px-8 h-14">
                  <Link to="/login?tab=signup">
                    Sign Up Free
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-lg px-8 h-14 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                  <Link to="/demo">
                    <Calendar className="mr-2 w-5 h-5" />
                    Book a Demo
                  </Link>
                </Button>
              </div>
              
              {/* Quick Stats */}
              {stats && (
                <div className="mt-16 flex flex-wrap justify-center gap-8 md:gap-16 text-center">
                  <div>
                    <div className="text-3xl md:text-4xl font-bold text-primary">{stats.totalMerchants}+</div>
                    <div className="text-sm text-muted-foreground mt-1">Businesses</div>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-bold text-primary">{formatCompactNumber(stats.totalRevenue)}</div>
                    <div className="text-sm text-muted-foreground mt-1">Processed</div>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-bold text-foreground">3x</div>
                    <div className="text-sm text-muted-foreground mt-1">More Conversions</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Decorative gradient */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(var(--primary)/0.1),transparent_50%)] pointer-events-none" />
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="container py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Three Simple Steps</h2>
            <p className="text-xl text-muted-foreground">Simple for you, delightful for your customers</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="border-2 border-border hover:border-primary/50 transition-colors text-center">
              <CardContent className="pt-8 pb-6 px-6">
                <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  1
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Add to Split Cart</h3>
                <p className="text-muted-foreground text-sm">
                  Customer picks a product and invites friends
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-border hover:border-primary/50 transition-colors text-center">
              <CardContent className="pt-8 pb-6 px-6">
                <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  2
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Friends Chip In</h3>
                <p className="text-muted-foreground text-sm">
                  One link, one tap — everyone contributes
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-border hover:border-primary/50 transition-colors text-center">
              <CardContent className="pt-8 pb-6 px-6">
                <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  3
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Gift Delivered</h3>
                <p className="text-muted-foreground text-sm">
                  Order placed automatically. Magic! ✨
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Features Section */}
        <section className="bg-card border-y border-border">
          <div className="container py-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Built for Growth</h2>
              <p className="text-xl text-muted-foreground">Everything you need to unlock group purchases</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Store className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Multi-Platform</h3>
                  <p className="text-muted-foreground text-sm">
                    Shopify, WooCommerce, or custom — we integrate seamlessly
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Any Payment Gateway</h3>
                  <p className="text-muted-foreground text-sm">
                    Razorpay, Stripe, PayU — use what you have
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Auto Reminders</h3>
                  <p className="text-muted-foreground text-sm">
                    Smart notifications ensure payments complete on time
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Inventory Protection</h3>
                  <p className="text-muted-foreground text-sm">
                    Automatic reservation when a group purchase starts
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">5-Minute Setup</h3>
                  <p className="text-muted-foreground text-sm">
                    Install widget, configure settings, go live
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Any Group Size</h3>
                  <p className="text-muted-foreground text-sm">
                    From 2 friends to 20+ contributors
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Preview Section */}
        <section id="pricing" className="container py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-muted-foreground">Choose what fits your needs. Start free, scale as you grow.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Merchants */}
            <Card className="border-2 border-border hover:border-primary/50 transition-colors relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 via-fuchsia-500 to-blue-500" />
              <CardContent className="pt-8 pb-6 px-6">
                <div className="flex items-center gap-2 mb-4">
                  <Store className="w-6 h-6 text-primary" />
                  <h3 className="text-lg font-bold text-foreground">For Merchants</h3>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Sparkles className="w-4 h-4 text-green-500" />
                    <span>14-day free trial</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CreditCard className="w-4 h-4 text-fuchsia-500" />
                    <span>From ₹999/mo or 2% per split</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="w-4 h-4 text-blue-500" />
                    <span>Unlimited contributors</span>
                  </div>
                </div>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/pricing?tab=merchant">View Plans</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Professionals */}
            <Card className="border-2 border-cyan-500/30 hover:border-cyan-500/50 transition-colors relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-400 to-teal-500" />
              <CardContent className="pt-8 pb-6 px-6">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-6 h-6 text-cyan-600" />
                  <h3 className="text-lg font-bold text-foreground">For Professionals</h3>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Sparkles className="w-4 h-4 text-cyan-500" />
                    <span className="font-medium text-cyan-600">3 months FREE trial</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CreditCard className="w-4 h-4 text-teal-500" />
                    <span>₹10/payment or ₹500/mo</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4 text-cyan-500" />
                    <span>Volume discounts available</span>
                  </div>
                </div>
                <Button asChild className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white">
                  <Link to="/pricing?tab=professional">View Plans</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Customers */}
            <Card className="border-2 border-border hover:border-pink-500/30 transition-colors relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-500 via-violet-500 to-emerald-500" />
              <CardContent className="pt-8 pb-6 px-6">
                <div className="flex items-center gap-2 mb-4">
                  <Gift className="w-6 h-6 text-pink-500" />
                  <h3 className="text-lg font-bold text-foreground">For Customers</h3>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Sparkles className="w-4 h-4 text-pink-500" />
                    <span className="font-medium text-pink-600">Always FREE</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CreditCard className="w-4 h-4 text-violet-500" />
                    <span>No fees for participants</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Gift className="w-4 h-4 text-emerald-500" />
                    <span>Split gifts & group payments</span>
                  </div>
                </div>
                <Button asChild variant="outline" className="w-full border-pink-500/30 text-pink-600 hover:bg-pink-50">
                  <Link to="/my-purchases">Track Purchases</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container py-20">
          <Card className="border-2 border-primary/20 bg-card overflow-hidden">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Left - Content */}
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium w-fit mb-4">
                    <Sparkles className="w-3.5 h-3.5" />
                    Free to start
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                    Ready to unlock group purchases?
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Join businesses boosting AOV with birthdays, weddings & celebrations.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button asChild size="lg" className="px-8">
                      <Link to="/login">
                        Get Started
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </Button>
                    <Button asChild variant="ghost" size="lg">
                      <Link to="/pricing">View Pricing</Link>
                    </Button>
                  </div>
                </div>
                {/* Right - Visual */}
                <div className="hidden md:flex items-center justify-center bg-gradient-to-br from-primary/5 to-accent/10 p-12">
                  <div className="grid grid-cols-2 gap-4 max-w-xs">
                    <div className="bg-background rounded-xl p-4 shadow-sm border border-border">
                      <Gift className="w-8 h-8 text-primary mb-2" />
                      <div className="text-2xl font-bold text-foreground">3x</div>
                      <div className="text-xs text-muted-foreground">Higher AOV</div>
                    </div>
                    <div className="bg-background rounded-xl p-4 shadow-sm border border-border">
                      <Users className="w-8 h-8 text-primary mb-2" />
                      <div className="text-2xl font-bold text-foreground">5+</div>
                      <div className="text-xs text-muted-foreground">Avg. contributors</div>
                    </div>
                    <div className="bg-background rounded-xl p-4 shadow-sm border border-border col-span-2">
                      <Clock className="w-8 h-8 text-primary mb-2" />
                      <div className="text-2xl font-bold text-foreground">5 mins</div>
                      <div className="text-xs text-muted-foreground">Setup time</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
      

      <footer className="border-t border-border bg-card">
        <div className="container py-10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-primary" />
              <span className="font-bold text-foreground">Shakatah</span>
              <span className="text-muted-foreground text-sm ml-2">Group gifting & payments for e-commerce</span>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-4 md:gap-6 text-sm text-muted-foreground">
              <Link to="/my-purchases" className="hover:text-foreground transition-colors">Track Your Purchase</Link>
              <Link to="/pricing" className="hover:text-foreground transition-colors">Pricing</Link>
              <Link to="/faq" className="hover:text-foreground transition-colors">FAQ</Link>
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms-of-service" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
      
      <FAQChatBot context="customer" />
    </div>
  );
}

export default Index;
